import { getPrimaryColor, PAGE_NAME, Styles } from '@orcar/common-d2c';
import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { app } from '@/constants/common';

const svg = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

/** 서비스 소개 카드 — 각 항목 라인아트 일러스트 + 실제 존재하는 화면으로 연결 */
const SERVICES: {
  title: string;
  desc: string;
  moveTo?: string;
  art: ReactNode;
}[] = [
  {
    title: '실시간 렌트카 예약',
    desc: '원하는 날짜·차종을 실시간으로 확인하고 즉시 예약하세요.',
    moveTo: PAGE_NAME.search,
    art: (
      <svg viewBox='0 0 48 48' {...svg}>
        <rect x='8' y='10' width='32' height='30' rx='4' />
        <path d='M8 18h32M16 6v8M32 6v8' />
        <path d='M16 26h4M24 26h4M16 32h4M24 32h4' />
      </svg>
    ),
  },
  {
    title: '무료 셔틀버스',
    desc: '제주공항 ↔ 본사 무료 셔틀 운행. 운행시간·탑승장소를 확인하세요.',
    moveTo: '/guide/shuttle',
    art: (
      <svg viewBox='0 0 48 48' {...svg}>
        <rect x='7' y='10' width='34' height='22' rx='4' />
        <path d='M7 21h34M13 15h9M26 15h9' />
        <path d='M14 32v4M34 32v4' />
        <path d='M15 26h.01M33 26h.01' />
      </svg>
    ),
  },
  {
    title: '완전면책 보험',
    desc: '자기부담금 없이 무제한 보장. 현장출동 서비스까지 포함해 안심 여행.',
    moveTo: '/guide/insurance',
    art: (
      <svg viewBox='0 0 48 48' {...svg}>
        <path d='M24 6l15 6v9c0 9-6 16-15 19-9-3-15-10-15-19v-9l15-6z' />
        <path d='M17 24l5 5 9-9' />
      </svg>
    ),
  },
  {
    title: '블랙박스 · 내비 기본 장착',
    desc: '전 차량에 블랙박스와 내비게이션이 기본 장착되어 안심하고 편하게 운전하세요.',
    art: (
      <svg viewBox='0 0 48 48' {...svg}>
        <rect x='8' y='17' width='32' height='19' rx='4' />
        <circle cx='24' cy='26' r='6' />
        <path d='M18 17l3-4h6l3 4' />
      </svg>
    ),
  },
  {
    title: '친구추천 · 마일리지',
    desc: '친구를 초대하면 너도 나도 적립! 재방문 마일리지 혜택까지.',
    art: (
      <svg viewBox='0 0 48 48' {...svg}>
        <rect x='9' y='20' width='30' height='19' rx='3' />
        <path d='M9 27h30M24 20v19' />
        <path d='M24 20c-4-8-13-6-13-1 0 3 5 3 13 1z' />
        <path d='M24 20c4-8 13-6 13-1 0 3-5 3-13 1z' />
      </svg>
    ),
  },
  {
    title: '펫과 함께',
    desc: '반려동물 동반 여행도 가능합니다. 전용 예약 경로로 문의해 주세요.',
    art: (
      <svg viewBox='0 0 48 48' {...svg}>
        <circle cx='16' cy='19' r='3' />
        <circle cx='24' cy='15' r='3' />
        <circle cx='32' cy='19' r='3' />
        <path d='M24 25c-5 0-9 4-9 8 0 3 3 5 9 5s9-2 9-5c0-4-4-8-9-8z' />
      </svg>
    ),
  },
];

const ServicePage = () => {
  const navigate = useNavigate();

  return (
    <Center>
      <Inner>
        <Title>서비스</Title>
        <Lead>제주로렌트카가 제공하는 서비스입니다.</Lead>
        <Grid>
          {SERVICES.map((s) => (
            <Card
              key={s.title}
              clickable={!!s.moveTo}
              onClick={() => s.moveTo && navigate(s.moveTo)}
            >
              <Art aria-hidden>{s.art}</Art>
              <CardTitle>{s.title}</CardTitle>
              <CardDesc>{s.desc}</CardDesc>
              {s.moveTo && <More>자세히 보기 →</More>}
            </Card>
          ))}
        </Grid>
      </Inner>
    </Center>
  );
};

export default ServicePage;

const Center = styled.div`
  display: flex;
  justify-content: center;
`;

const Inner = styled.div`
  width: 100%;
  max-width: 1024px;
  padding: 32px 20px 64px;
`;

const Title = styled.h1`
  ${Styles.FONT.H2};
  color: ${Styles.COLOR.GREY_01};
  margin: 0;
`;

const Lead = styled.p`
  ${Styles.FONT.B1};
  color: ${Styles.COLOR.GREY_03};
  margin: 8px 0 28px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div<{ clickable?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 24px;
  border: 1px solid ${Styles.COLOR.GREY_05};
  border-radius: 12px;
  background-color: ${Styles.COLOR.WHITE};
  cursor: ${({ clickable }) => (clickable ? 'pointer' : 'default')};
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;

  &:hover {
    ${({ clickable }) =>
      clickable &&
      `border-color: ${getPrimaryColor(app)}; box-shadow: 0 8px 20px rgba(0,0,0,0.06);`}
  }
`;

const Art = styled.div`
  width: 64px;
  height: 64px;
  display: grid;
  place-items: center;
  border-radius: 16px;
  margin-bottom: 4px;
  color: ${getPrimaryColor(app)};
  background-color: ${getPrimaryColor(app)}14;

  svg {
    width: 34px;
    height: 34px;
  }
`;

const CardTitle = styled.h3`
  ${Styles.FONT.H4};
  color: ${Styles.COLOR.GREY_01};
  margin: 0;
`;

const CardDesc = styled.p`
  ${Styles.FONT.B2};
  color: ${Styles.COLOR.GREY_03};
  margin: 0;
`;

const More = styled.span`
  margin-top: auto;
  ${Styles.FONT.B2};
  font-weight: 700;
  color: ${getPrimaryColor(app)};
`;
